/**
 * Where this partner has taken over gating, as one list.
 *
 * Read-only on purpose: editing happens on the plugin, under the layer
 * rail, because that is where the field schema lives. This card answers a
 * question the plugin pages cannot — "what has this partner locked?" —
 * which would otherwise mean opening every plugin in turn.
 *
 * The open-to-organizations count is the number that matters: a row with
 * none has closed the whole plugin to its organizations.
 *
 * The rows do not link. A config row is keyed by the registry id, the
 * plugin route by the catalogue name, and the two are not always the same
 * string — a link built from the wrong one lands on a missing page, which
 * is worse than no link.
 */
import { listPartnerCapabilityConfigs } from '@optum/api-client/endpoints/partner-capability-configs';
import type { FieldScopes } from '@optum/api-client/types';
import { useQuery } from '@tanstack/react-query';

import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../ui/AsyncState.js';
import { qk } from '../../lib/query-keys.js';

interface Row {
  pluginId: string;
  fieldCount: number;
  openToOrganizations: number;
}

function countOpen(scopes: FieldScopes): number {
  return Object.values(scopes).filter((flags) => flags.organization === true).length;
}

export function PartnerConfigRows({ partnerId }: { partnerId: string }) {
  const capabilities = useQuery({
    queryKey: qk.partnerCapabilityConfigs(partnerId),
    queryFn: () => listPartnerCapabilityConfigs(partnerId),
  });
  if (capabilities.isPending) return <LoadingBlock />;
  if (capabilities.isError) {
    const error = capabilities.error;
    return <ErrorBlock message={error instanceof Error ? error.message : 'Failed to load'} />;
  }

  const rows: Row[] = (capabilities.data ?? []).map<Row>((row) => ({
    pluginId: row.capability_id,
    fieldCount: Object.keys(row.config).length,
    openToOrganizations: countOpen(row.field_scopes),
  }));

  if (rows.length === 0) {
    return (
      <EmptyBlock>
        No configuration rows. This partner&apos;s organizations are gated by the global layer.
      </EmptyBlock>
    );
  }

  return (
    <div
      style={{
        borderRadius: 'var(--radius-card)',
        border: '1px solid var(--border-hairline)',
        background: 'var(--surface-card)',
      }}
    >
      {rows.map((row) => (
        <div
          key={row.pluginId}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 11,
            borderBottom: '1px solid var(--border-hairline)',
            borderColor: 'var(--border-hairline)',
            paddingLeft: 16,
            paddingRight: 16,
            paddingTop: 11,
            paddingBottom: 11,
          }}
        >
          <span
            style={{ flex: '1 1 0%', fontSize: 'var(--text-meta-lg)', color: 'var(--text-strong)' }}
            className="mono"
          >
            {row.pluginId}
          </span>
          <span style={{ fontSize: 'var(--text-meta-lg)', color: 'var(--text-muted)' }}>
            {row.fieldCount} {row.fieldCount === 1 ? 'field' : 'fields'} · {row.openToOrganizations}{' '}
            open to orgs
          </span>
        </div>
      ))}
      <p
        style={{
          paddingLeft: 16,
          paddingRight: 16,
          paddingTop: 11,
          paddingBottom: 11,
          fontSize: 'var(--text-meta-lg)',
          color: 'var(--text-muted)',
        }}
      >
        Edit on the plugin&apos;s Configuration tab with the layer rail set to Partner.
      </p>
    </div>
  );
}
